import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  TrendingUp,
  Database,
  Mail,
  MessageSquare,
  Webhook,
  Loader2,
  CheckCircle2,
  ExternalLink,
  Eye,
  EyeOff,
  Bot,
} from 'lucide-react';
import { fetchConfig, updateConfig, testService } from '../api/admin';

const integrations = [
  {
    id: 'moz',
    name: 'Moz API',
    icon: TrendingUp,
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-500/10',
    description: 'Domain Authority, Page Authority, and Spam Score metrics for backlink quality analysis.',
    docsUrl: 'https://moz.com/help/links-api',
    fields: [
      { key: 'moz_access_id', label: 'Access ID', type: 'text', placeholder: 'mozscape-xxxxx' },
      { key: 'moz_secret_key', label: 'Secret Key', type: 'password', placeholder: 'Enter secret key' },
    ],
    configKey: 'moz',
  },
  {
    id: 'dataforseo',
    name: 'DataForSEO',
    icon: Database,
    iconColor: 'text-emerald-500',
    iconBg: 'bg-emerald-500/10',
    description: 'Domain Rank, Spam Score, and Referring Domains data. Supports batch lookups up to 1000 domains.',
    docsUrl: 'https://docs.dataforseo.com/v3/backlinks/overview/',
    fields: [
      { key: 'dataforseo_login', label: 'API Login', type: 'text', placeholder: 'your@email.com' },
      { key: 'dataforseo_password', label: 'API Password', type: 'password', placeholder: 'Enter password' },
    ],
    configKey: 'dataforseo',
  },
  {
    id: 'scrapingrobot',
    name: 'Scraping Robot',
    icon: Bot,
    iconColor: 'text-cyan-500',
    iconBg: 'bg-cyan-500/10',
    description: 'Alternative SERP provider for keyword rank tracking. 5,000 free scrapes/month. Use instead of DataForSEO for rank checks.',
    docsUrl: 'https://scrapingrobot.com/docs',
    fields: [
      { key: 'scrapingrobot_token', label: 'API Token', type: 'password', placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
    ],
    configKey: 'scrapingrobot',
  },
  {
    id: 'emailit',
    name: 'Emailit',
    icon: Mail,
    iconColor: 'text-orange-500',
    iconBg: 'bg-orange-500/10',
    description: 'Email alert delivery when backlink status changes. Sends notifications to project alert emails.',
    docsUrl: 'https://www.emailit.com/docs',
    fields: [
      { key: 'emailit_api_key', label: 'API Key', type: 'password', placeholder: 'Enter API key' },
    ],
    configKey: 'emailit',
  },
  {
    id: 'slack',
    name: 'Slack',
    icon: MessageSquare,
    iconColor: 'text-purple-500',
    iconBg: 'bg-purple-500/10',
    description: 'Receive backlink status change alerts directly in your Slack channel via incoming webhook.',
    docsUrl: 'https://api.slack.com/messaging/webhooks',
    fields: [
      { key: 'slack_webhook', label: 'Webhook URL', type: 'url', placeholder: 'https://hooks.slack.com/services/...' },
    ],
    configKey: 'slack',
  },
  {
    id: 'n8n',
    name: 'n8n DataForSEO',
    icon: Webhook,
    iconColor: 'text-rose-500',
    iconBg: 'bg-rose-500/10',
    description: 'Fetch DFS metrics via n8n workflow — no $100/mo DataForSEO subscription needed. Also powers custom alert notifications.',
    docsUrl: 'https://docs.n8n.io/integrations/builtin/core-nodes/n8n-nodes-base.webhook/',
    fields: [
      { key: 'n8n_dataforseo_webhook', label: 'DFS Metrics Webhook URL', type: 'url', placeholder: 'https://your-n8n.app/webhook/dataforseo-metrics' },
    ],
    configKey: 'n8n_dfs',
  },
];

const SERP_PROVIDERS = [
  {
    value: 'dataforseo',
    label: 'DataForSEO',
    description: 'Industry-standard SERP API. Supports async batch + live checks.',
  },
  {
    value: 'scrapingrobot',
    label: 'Scraping Robot',
    description: '5,000 free scrapes/month. Live checks only (no async batch).',
  },
];

function SerpProviderSelector({ currentProvider, onSave, isSaving }) {
  const [provider, setProvider] = useState(currentProvider || 'dataforseo');

  const handleChange = (newProvider) => {
    setProvider(newProvider);
    onSave({ serp_provider: newProvider });
  };

  return (
    <div className="glow-card-filled p-6">
      <h3 className="font-semibold text-foreground mb-1">SERP Provider for Rankings</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Choose which service to use for keyword rank checking.
      </p>
      <div className="space-y-2">
        {SERP_PROVIDERS.map((p) => (
          <label
            key={p.value}
            className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${
              provider === p.value
                ? 'border-primary/50 bg-primary/5'
                : 'border-border/50 hover:border-border'
            }`}
          >
            <input
              type="radio"
              name="serp_provider"
              value={p.value}
              checked={provider === p.value}
              onChange={() => handleChange(p.value)}
              disabled={isSaving}
              className="mt-0.5 accent-primary"
            />
            <div>
              <span className="text-sm font-medium text-foreground">{p.label}</span>
              <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

const DFS_MODES = [
  {
    value: 'auto',
    label: 'Auto (recommended)',
    description: '1-2 domains: Live ($0.02/ea) | 3+: Batch ($0.06 flat)',
  },
  {
    value: 'batch',
    label: 'Always Batch',
    description: '3 bulk API calls, $0.06 for up to 1000 domains',
  },
  {
    value: 'live',
    label: 'Always Live',
    description: '1 API call per domain, $0.02 each',
  },
];

function DFSModeSelector({ currentMode, onSave, isSaving }) {
  const [mode, setMode] = useState(currentMode || 'auto');

  const handleModeChange = (newMode) => {
    setMode(newMode);
    onSave({ dfs_mode: newMode });
  };

  return (
    <div className="mt-4 pt-4 border-t border-border/50">
      <label className="block text-xs font-medium text-foreground mb-2">API Mode</label>
      <div className="space-y-2">
        {DFS_MODES.map((m) => (
          <label
            key={m.value}
            className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${
              mode === m.value
                ? 'border-primary/50 bg-primary/5'
                : 'border-border/50 hover:border-border'
            }`}
          >
            <input
              type="radio"
              name="dfs_mode"
              value={m.value}
              checked={mode === m.value}
              onChange={() => handleModeChange(m.value)}
              disabled={isSaving}
              className="mt-0.5 accent-primary"
            />
            <div>
              <span className="text-sm font-medium text-foreground">{m.label}</span>
              <p className="text-xs text-muted-foreground mt-0.5">{m.description}</p>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

const DFS_PROVIDERS = [
  {
    value: 'dataforseo',
    label: 'DataForSEO (Direct)',
    description: 'Use your own DFS API credentials. Requires $100/mo minimum subscription.',
  },
  {
    value: 'n8n',
    label: 'n8n Workflow',
    description: 'Route through n8n webhook. No DFS subscription needed.',
  },
];

function DFSProviderSelector({ currentProvider, onSave, isSaving }) {
  const [provider, setProvider] = useState(currentProvider || 'dataforseo');

  const handleChange = (newProvider) => {
    setProvider(newProvider);
    onSave({ dfs_provider: newProvider });
  };

  return (
    <div className="glow-card-filled p-6">
      <h3 className="font-semibold text-foreground mb-1">DFS Metrics Provider</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Choose how to fetch DataForSEO backlink metrics (Domain Rank, Spam Score, Referring Domains).
      </p>
      <div className="space-y-2">
        {DFS_PROVIDERS.map((p) => (
          <label
            key={p.value}
            className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${
              provider === p.value
                ? 'border-primary/50 bg-primary/5'
                : 'border-border/50 hover:border-border'
            }`}
          >
            <input
              type="radio"
              name="dfs_provider"
              value={p.value}
              checked={provider === p.value}
              onChange={() => handleChange(p.value)}
              disabled={isSaving}
              className="mt-0.5 accent-primary"
            />
            <div>
              <span className="text-sm font-medium text-foreground">{p.label}</span>
              <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function IntegrationCard({ integration, configData, onSave, onTest, isSaving, isTesting }) {
  const [values, setValues] = useState({});
  const [showSecrets, setShowSecrets] = useState({});

  const isConfigured = configData?.configured?.[integration.configKey];
  const Icon = integration.icon;

  const handleSave = () => {
    const data = {};
    let hasValue = false;
    for (const field of integration.fields) {
      if (values[field.key]) {
        data[field.key] = values[field.key];
        hasValue = true;
      }
    }
    if (!hasValue) {
      toast.error('Please enter at least one field');
      return;
    }
    onSave(data);
    setValues({});
  };

  const toggleSecret = (key) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="glow-card-filled p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-lg ${integration.iconBg}`}>
            <Icon className={`h-5 w-5 ${integration.iconColor}`} />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{integration.name}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{integration.description}</p>
          </div>
        </div>
        {isConfigured ? (
          <span className="flex items-center gap-1 text-xs text-success bg-success/15 px-2.5 py-1 rounded-full font-medium">
            <CheckCircle2 className="h-3 w-3" />
            Connected
          </span>
        ) : (
          <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full border border-border">
            Not configured
          </span>
        )}
      </div>

      <div className="space-y-3 mb-4">
        {integration.fields.map((field) => (
          <div key={field.key}>
            <label className="block text-xs text-muted-foreground mb-1">{field.label}</label>
            <div className="relative">
              <input
                type={field.type === 'password' && !showSecrets[field.key] ? 'password' : 'text'}
                value={values[field.key] || ''}
                onChange={(e) => setValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                placeholder={configData?.config?.[field.key] || field.placeholder}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent pr-10"
              />
              {field.type === 'password' && (
                <button
                  type="button"
                  onClick={() => toggleSecret(field.key)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showSecrets[field.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            </div>
          </div>
        ))}
        {integration.id === 'dataforseo' && (
          <DFSModeSelector
            currentMode={configData?.config?.dfs_mode || 'auto'}
            onSave={onSave}
            isSaving={isSaving}
          />
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-border/50">
        <a
          href={integration.docsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
        >
          Documentation
          <ExternalLink className="h-3 w-3" />
        </a>
        <div className="flex items-center gap-2">
          {isConfigured && (
            <button
              onClick={() => onTest(integration.id === 'n8n' ? 'n8n_dfs' : integration.id)}
              disabled={isTesting}
              className="px-3 py-1.5 text-xs text-primary hover:text-primary/80 border border-primary/30 hover:border-primary/50 rounded-lg transition-colors disabled:opacity-50"
            >
              {isTesting ? 'Testing...' : 'Test Connection'}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Integrations() {
  const queryClient = useQueryClient();

  const { data: configData, isLoading } = useQuery({
    queryKey: ['admin-config'],
    queryFn: fetchConfig,
  });

  const updateMutation = useMutation({
    mutationFn: updateConfig,
    onSuccess: () => {
      toast.success('Configuration saved');
      queryClient.invalidateQueries(['admin-config']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to save configuration');
    },
  });

  const testMutation = useMutation({
    mutationFn: testService,
    onSuccess: (data) => {
      toast.success(data.message || 'Connection successful');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Connection test failed');
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <div className="h-8 w-48 bg-muted rounded animate-pulse" />
          <div className="h-4 w-72 bg-muted rounded animate-pulse mt-2" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-64 bg-muted/50 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Integrations</h1>
        <p className="text-muted-foreground mt-1">
          Connect external services to enhance backlink monitoring and alerts.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {integrations.map((integration) => (
          <IntegrationCard
            key={integration.id}
            integration={integration}
            configData={configData}
            onSave={(data) => updateMutation.mutate(data)}
            onTest={(service) => testMutation.mutate(service)}
            isSaving={updateMutation.isPending}
            isTesting={testMutation.isPending}
          />
        ))}
      </div>

      {(configData?.configured?.dataforseo || configData?.configured?.scrapingrobot) && (
        <SerpProviderSelector
          currentProvider={configData?.config?.serp_provider || 'dataforseo'}
          onSave={(data) => updateMutation.mutate(data)}
          isSaving={updateMutation.isPending}
        />
      )}

      {(configData?.configured?.dataforseo || configData?.configured?.n8n_dfs) && (
        <DFSProviderSelector
          currentProvider={configData?.config?.dfs_provider || 'dataforseo'}
          onSave={(data) => updateMutation.mutate(data)}
          isSaving={updateMutation.isPending}
        />
      )}
    </div>
  );
}
